// React imports
import React, { useState } from 'react';

// Third-party imports
import clsx from 'clsx';

// Types
import { Discount } from '../types';
import { PaginationProps } from 'components/UI/V3/Pagination/Pagination';

// UI Components
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { Table } from 'components/UI/V3/Table/Table';

// Local Components
import { DiscountsRow } from '../DiscountsRow/DiscountsRow';
import DiscountsIsEmpty from '../DiscountsIsEmpty/DiscountsIsEmpty';

// Context
import { useDiscounts } from '../../DiscountsProvider';

// Styles
import styles from './DiscountsTable.module.scss';

interface DiscountsTableProps {
    discounts: Discount[];
    onEdit: (_id: number) => void;
    onView: (_id: number) => void;
    onToggleStatus: (_id: number) => void;
    onDelete: (_id: number) => void;
    className?: string;
}

export const DiscountsTable: React.FC<DiscountsTableProps> = ({
    discounts,
    onEdit,
    onView,
    onToggleStatus,
    onDelete,
    className
}) => {
    const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);

    const { page, totalPages, size, isLoading, setPage, setSize, searchText, selectedFilter } = useDiscounts();

    const pagination: PaginationProps = {
        page,
        totalPages,
        size,
        isLoading
    };

    const isEmpty = discounts.length === 0;

    const renderTableContent = () => {
        // Show filtered empty state only when there's an actual search or when filter is not 'all' or 'active'
        // (since 'active' is the default view, we treat it as "no filter")
        const hasActiveFilter = searchText || (selectedFilter !== 'all' && selectedFilter !== 'active');

        if (isEmpty && hasActiveFilter && !isLoading) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={6}
                            style={{ textAlign: 'center', padding: '0' }}
                        >
                            <DiscountsIsEmpty
                                title="No discounts found"
                                text="No Discounts match your filter selection. Try changing or clearing the filter to see more."
                            />
                        </td>
                    </tr>
                </TableBody>
            );
        }
        // Empty state - no discounts at all
        if (isEmpty && !isLoading) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={6}
                            style={{ textAlign: 'center', padding: '0' }}
                        >
                            <DiscountsIsEmpty
                                title="No discounts yet"
                                text="Add your first discount to start rewarding special clients or multi-pet families."
                            />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        // Always show existing data - loading spinner only in pagination
        return (
            <TableBody>
                {discounts.map((discount, index) => (
                    <DiscountsRow
                        key={discount.id}
                        discount={discount}
                        isLast={discounts.length === index + 1}
                        onEdit={onEdit}
                        onView={onView}
                        onToggleStatus={onToggleStatus}
                        onDelete={onDelete}
                        isDropdownOpen={openDropdownId === discount.id}
                        onDropdownToggle={isOpen => setOpenDropdownId(isOpen ? discount.id : null)}
                    />
                ))}
            </TableBody>
        );
    };

    return (
        <Table
            pagination={pagination}
            onPageChange={setPage}
            onSizeChange={setSize}
            fullHeight
            firstColumnFixed
            className={clsx(styles.table, className)}
        >
            <TableHeader className={styles.tableHeader}>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Services</TableHeaderCell>
                <TableHeaderCell>Apply To</TableHeaderCell>
                <TableHeaderCell>Used</TableHeaderCell>
                <TableHeaderCell />
            </TableHeader>

            {renderTableContent()}
        </Table>
    );
};
